import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Lock, X, Ticket, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { renderPayPalButtons } from "@/lib/paypal";
import api from "@/lib/api";

/**
 * Unlock button for the certificate PDF. On first click it reveals PayPal Smart Buttons
 * and a coupon-code field for privileged codes.
 */
export default function PayPalUnlockButton({
  certId,
  onSuccess,
  priceLabel = "$30",
  variant = "light",
  testId = "unlock-cert-btn",
}) {
  const [open, setOpen] = useState(false);
  const [couponOpen, setCouponOpen] = useState(false);
  const [coupon, setCoupon] = useState("");
  const [redeeming, setRedeeming] = useState(false);
  const [testCode, setTestCode] = useState(null); // if set, PayPal renders with $3 test amount
  const containerRef = useRef(null);
  const cleanupRef = useRef(null);

  useEffect(() => {
    if (!open || !containerRef.current) return;
    let cancelled = false;
    (async () => {
      const cleanup = await renderPayPalButtons(containerRef.current, certId, onSuccess, testCode);
      if (cancelled) {
        cleanup?.();
      } else {
        cleanupRef.current = cleanup;
      }
    })();
    return () => {
      cancelled = true;
      cleanupRef.current?.();
      cleanupRef.current = null;
    };
  }, [open, certId, onSuccess, testCode]);

  const redeemCoupon = async () => {
    const code = coupon.trim();
    if (!code) return toast.error("Enter a coupon code");
    setRedeeming(true);
    try {
      // Try full-redeem first (100% off codes like IMCACFREE)
      await api.post(`/payment/redeem-coupon/${certId}`, { code });
      toast.success("Coupon accepted — certificate unlocked!");
      setCouponOpen(false);
      setCoupon("");
      onSuccess?.();
    } catch (err) {
      const detail = err.response?.data?.detail || "";
      // If server rejected, try test-mode: fall through and let PayPal render at the discounted price
      if (detail.toLowerCase().includes("invalid")) {
        // Might be a discount code (like TESTPAY10) — pass it to PayPal createOrder for server-side validation
        setTestCode(code.toUpperCase());
        toast(`Code applied — PayPal will validate the discount`, { icon: "🧪" });
        setCouponOpen(false);
        setCoupon("");
      } else {
        toast.error(detail || "Coupon redemption failed");
      }
    } finally {
      setRedeeming(false);
    }
  };

  if (!open) {
    return (
      <Button
        onClick={() => setOpen(true)}
        className="bg-red-600 hover:bg-red-700 text-white"
        data-testid={testId}
      >
        <Lock className="mr-2 h-4 w-4" /> Unlock certificate — {priceLabel}
      </Button>
    );
  }

  const wrapperBg = variant === "dark" ? "bg-zinc-800 border-zinc-700" : "bg-white border-zinc-200";
  const textColor = variant === "dark" ? "text-zinc-300" : "text-zinc-600";
  const linkColor = variant === "dark" ? "text-red-400 hover:text-red-300" : "text-red-700 hover:text-red-900";

  return (
    <div className={`w-full sm:max-w-sm rounded-md border p-4 ${wrapperBg}`} data-testid="paypal-unlock-panel">
      <div className={`flex items-center justify-between mb-3 text-xs font-mono uppercase tracking-widest ${textColor}`}>
        <span>Pay with PayPal — {priceLabel} USD</span>
        <button
          type="button"
          onClick={() => setOpen(false)}
          className="hover:opacity-70"
          aria-label="Close"
          data-testid="paypal-unlock-close"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
      <div ref={containerRef} data-testid="paypal-buttons-container" />
      <div className={`mt-3 text-[11px] ${textColor}`}>
        Secured by PayPal. No card details are shared with us.
      </div>

      {/* Coupon (privileged; not advertised publicly) */}
      <div className="mt-4 pt-4 border-t border-zinc-700/40">
        {!couponOpen ? (
          <button
            type="button"
            onClick={() => setCouponOpen(true)}
            className={`text-[11px] font-mono uppercase tracking-widest inline-flex items-center gap-1.5 ${linkColor}`}
            data-testid="have-coupon-toggle"
          >
            <Ticket className="h-3 w-3" /> Have an access code?
          </button>
        ) : (
          <div className="space-y-2" data-testid="coupon-input-panel">
            <div className="flex items-center gap-2">
              <Input
                value={coupon}
                onChange={(e) => setCoupon(e.target.value.toUpperCase())}
                onKeyDown={(e) => e.key === "Enter" && !redeeming && redeemCoupon()}
                placeholder="ENTER CODE"
                className="h-9 uppercase font-mono tracking-widest text-sm"
                disabled={redeeming}
                data-testid="coupon-code-input"
              />
              <Button
                type="button"
                onClick={redeemCoupon}
                disabled={redeeming || !coupon.trim()}
                size="sm"
                className="bg-red-600 hover:bg-red-700 text-white h-9"
                data-testid="coupon-redeem-btn"
              >
                {redeeming ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Apply"}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
